import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";

interface TextRevealOverlayProps {
  /** Value to watch for changes (e.g. the paragraph text). A change re-plays
   * the reveal animation over the current children; the same value on
   * re-render (including the first mount) is a no-op. */
  watch: string;
  children: ReactNode;
  className?: string;
  /** When false, `watch` changes update the baseline WITHOUT playing the
   * reveal (used while an answer is streaming in token-by-token). Defaults to
   * true. Flipping false -> true (a stream settling into its final text) also
   * adopts the new baseline silently, so the stream->final handoff -- where the
   * streamed clean prose is replaced by the citation-annotated final answer --
   * does not flicker with a reveal the operator already watched arrive. */
  animate?: boolean;
}

// Must match the `text-reveal-shimmer` animation-duration in chat.css.
const REVEAL_ANIMATION_MS = 550;

const BLOCK_SELECTOR = "p, li, h1, h2, h3, h4, h5, h6, blockquote";

/**
 * Wraps each block element's (p, li, ...) children in one
 * <span class="text-reveal-line"> so the gradient clip in chat.css can use
 * `background-attachment: local` to run independently per rendered line
 * (see the comment there) instead of once across the block's whole
 * bounding box. One wrapper per block, not per line or per character --
 * line splitting is left entirely to CSS/the browser's own layout.
 *
 * Same undo-safe wrap/restore shape as ProposedAnswerParagraph's citation
 * highlightUndo: `undo` puts every block's original children back
 * byte-for-byte, so this is safe to run even if a citation hover highlight
 * is walking the same text mid-animation.
 */
// Exported for TextRevealOverlay.test.tsx: the wrap/undo pair is the risky
// part of this component (it mutates DOM React owns) and is worth pinning
// directly, not only through the rendered component.
export function wrapBlocksForReveal(root: HTMLElement): { count: number; undo: () => void } {
  const blocks = root.querySelectorAll<HTMLElement>(BLOCK_SELECTOR);
  const undoFns: Array<() => void> = [];

  blocks.forEach((block) => {
    const span = document.createElement("span");
    span.className = "text-reveal-line";
    while (block.firstChild) span.appendChild(block.firstChild);
    block.appendChild(span);
    undoFns.push(() => {
      // React may have re-rendered or unmounted the block since the wrap, so
      // the span can be detached or reparented by now. Restore relative to
      // wherever the span actually lives -- inserting against the original
      // `block` throws NotFoundError once that link is gone.
      const parent = span.parentNode;
      if (!parent) return;
      while (span.firstChild) parent.insertBefore(span.firstChild, span);
      span.remove();
    });
  });

  let undone = false;
  return {
    count: undoFns.length,
    // undo() is called from both the reveal timeout and the effect cleanup that
    // the timeout's setIsPlaying(false) triggers -- the second call must be a
    // no-op rather than re-walking an already-restored DOM.
    undo: () => {
      if (undone) return;
      undone = true;
      for (const fn of undoFns.reverse()) fn();
    },
  };
}

/**
 * Plays a precise, mechanical color-shift over `children` whenever `watch`
 * changes (revision select, regenerate) — a narrow brand-gradient band
 * sweeps across each rendered line in discrete steps(), clipped to the
 * glyphs themselves (background-clip: text) rather than tinting the whole
 * paragraph box, so it reads as the characters flickering through color
 * rather than a wipe/mask reveal. Matches the brand's mechanistic identity
 * (see DESIGN.md) rather than a soft fade.
 *
 * The text is wrapped for the ~550ms the animation runs, then restored
 * exactly (see wrapBlocksForReveal) — so this is safe to wrap around
 * ProposedAnswerParagraph without permanently disturbing its
 * citation-hover DOM manipulation.
 */
export function TextRevealOverlay({
  watch,
  children,
  className,
  animate = true,
}: TextRevealOverlayProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const previousRef = useRef(watch);
  const previousAnimateRef = useRef(animate);
  const [isPlaying, setIsPlaying] = useState(false);
  const reducedMotionRef = useRef(
    typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    // A stream just settled into its final text (animate false -> true): adopt
    // the new baseline without playing -- the operator already watched it stream.
    const settledFromStream = !previousAnimateRef.current && animate;
    previousAnimateRef.current = animate;
    if (previousRef.current === watch) return;
    previousRef.current = watch;
    if (!animate || reducedMotionRef.current || settledFromStream) return;
    // Off, then on next frame: forces a restart even if a previous play is
    // still in flight (its own effect cleanup below restores the DOM first).
    setIsPlaying(false);
    const raf = requestAnimationFrame(() => setIsPlaying(true));
    return () => cancelAnimationFrame(raf);
  }, [watch, animate]);

  // Layout effect, not a regular effect: its cleanup must run synchronously
  // during React's commit/deletion phase, before React removes host DOM
  // nodes. A passive-effect (useEffect) cleanup fires asynchronously after
  // commit -- too late if the parent unmounts this subtree (e.g.
  // AgentProposedAnswer swapping from the streaming view to
  // MarkdownAnswerEditor) while a block is still wrapped: React would try to
  // removeChild() directly from `block`, but the child is one level deeper
  // inside our leftover `span`, throwing NotFoundError. useLayoutEffect's
  // synchronous cleanup unwraps the span first, so React's own removal
  // always sees the DOM it expects.
  useLayoutEffect(() => {
    if (!isPlaying) return;
    const root = wrapperRef.current;
    if (!root) {
      setIsPlaying(false);
      return;
    }

    const { count, undo } = wrapBlocksForReveal(root);
    if (count === 0) {
      setIsPlaying(false);
      return;
    }
    const timeout = window.setTimeout(() => {
      undo();
      setIsPlaying(false);
    }, REVEAL_ANIMATION_MS);

    return () => {
      window.clearTimeout(timeout);
      undo();
    };
  }, [isPlaying]);

  return (
    <div ref={wrapperRef} className={`relative${className ? ` ${className}` : ""}`}>
      {children}
    </div>
  );
}
